import React, { useState } from 'react';
import { ChevronRight, Mail, Shield, Users, Zap, CheckCircle, Star } from 'lucide-react';

interface LandingPageProps {
  onNavigateToLogin: () => void;
  onStartOnboarding: (email: string) => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({
  onNavigateToLogin,
  onStartOnboarding
}) => {
  const [inviteEmail, setInviteEmail] = useState('');
  const [isStartingOnboarding, setIsStartingOnboarding] = useState(false);

  const handleStartOnboarding = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    
    setIsStartingOnboarding(true);
    onStartOnboarding(inviteEmail.trim());
  };

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Mission: <span className="gradient-text">Trimpossible</span>
            </h1>
            <p className="hero-subtitle">
              Transform Your Health. Achieve The Impossible. Start Your Journey Today.
            </p>
            <p className="hero-description">
              Join thousands of people who have transformed their lives with our comprehensive 
              health and wellness platform. Get personalized coaching, track your progress, 
              and achieve results that once seemed impossible.
            </p>
            
            <div className="hero-actions">
              <button 
                className="btn-primary"
                onClick={onNavigateToLogin}
              >
                Client Login
                <ChevronRight size={20} />
              </button>
            </div>
          </div>

          <div className="hero-image">
            <div className="hero-placeholder">
              <div className="stats-card">
                <div className="stat-item">
                  <span className="stat-number">10K+</span>
                  <span className="stat-label">Transformations</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">95%</span>
                  <span className="stat-label">Success Rate</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">24/7</span>
                  <span className="stat-label">Support</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Invitation Section */}
      <section className="invitation-section">
        <div className="invitation-card">
          <div className="invitation-header">
            <Mail className="invitation-icon" />
            <h2>Received an Invitation?</h2>
            <p>Complete your sign-up and start your transformation journey</p>
          </div>
          
          <form onSubmit={handleStartOnboarding} className="invitation-form">
            <div className="form-group">
              <input
                type="email"
                placeholder="Enter your email address"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
                disabled={isStartingOnboarding}
                className="email-input"
              />
              <button
                type="submit"
                disabled={!inviteEmail.trim() || isStartingOnboarding}
                className="complete-signup-btn"
              >
                {isStartingOnboarding ? 'Starting...' : 'Complete Sign-Up'}
                <ChevronRight size={18} />
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="section-header">
          <h2>Why Choose Mission: Trimpossible?</h2>
          <p>Everything you need to achieve lasting health transformation</p>
        </div>

        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">
              <Users size={32} />
            </div>
            <h3>Expert Coaching</h3>
            <p>Get personalized guidance from certified health professionals and transformation coaches.</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <Zap size={32} />
            </div>
            <h3>Rapid Results</h3>
            <p>See meaningful changes in weeks, not months, with our proven acceleration methods.</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <Shield size={32} />
            </div>
            <h3>Safe & Sustainable</h3>
            <p>Evidence-based approaches that prioritize your health and long-term success.</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <CheckCircle size={32} />
            </div>
            <h3>Proven System</h3>
            <p>Join the 95% of our clients who achieve their transformation goals within 90 days.</p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="section-header">
          <h2>Success Stories</h2>
          <p>Real people, real transformations</p>
        </div>

        <div className="testimonials-grid">
          <div className="testimonial-card">
            <div className="testimonial-rating">
              {[1, 2, 3, 4, 5].map(i => (
                <Star key={i} size={16} className="star-filled" />
              ))}
            </div>
            <p className="testimonial-text">
              "I lost 45 pounds and gained so much confidence. The coaching team made 
              the impossible feel achievable every step of the way."
            </p>
            <div className="testimonial-author">
              <strong>Sarah M.</strong>
              <span>Lost 45 lbs in 3 months</span>
            </div>
          </div>

          <div className="testimonial-card">
            <div className="testimonial-rating">
              {[1, 2, 3, 4, 5].map(i => (
                <Star key={i} size={16} className="star-filled" />
              ))}
            </div>
            <p className="testimonial-text">
              "The personalized approach and constant support kept me motivated. 
              I've maintained my results for over a year now!"
            </p>
            <div className="testimonial-author">
              <strong>Mike R.</strong>
              <span>Maintained 60 lb weight loss</span>
            </div>
          </div>

          <div className="testimonial-card">
            <div className="testimonial-rating">
              {[1, 2, 3, 4, 5].map(i => (
                <Star key={i} size={16} className="star-filled" />
              ))}
            </div>
            <p className="testimonial-text">
              "More than weight loss - I transformed my entire lifestyle. 
              My energy levels are through the roof!"
            </p>
            <div className="testimonial-author">
              <strong>Jennifer L.</strong>
              <span>Complete lifestyle transformation</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>Ready to Start Your Transformation?</h2>
          <p>Contact us today to learn more about our programs and get started on your journey.</p>
          <div className="cta-actions">
            <button className="btn-secondary">Learn More</button>
            <button className="btn-primary" onClick={onNavigateToLogin}>
              Client Access
            </button>
          </div>
        </div>
      </section>

      <style>{`
        .landing-page {
          min-height: 100vh;
          background: var(--bg-primary);
        }

        .hero-section {
          padding: 80px 24px;
          background: linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 100%);
        }

        .hero-content {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: center;
        }

        .hero-title {
          font-size: 3.5rem;
          font-weight: 800;
          color: var(--text-primary);
          margin-bottom: 16px;
          line-height: 1.1;
        }

        .gradient-text {
          background: linear-gradient(45deg, var(--accent-blue), var(--accent-green));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-subtitle {
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--text-secondary);
          margin-bottom: 20px;
        }

        .hero-description {
          font-size: 1.1rem;
          color: var(--text-tertiary);
          line-height: 1.6;
          margin-bottom: 32px;
        }

        .hero-actions {
          display: flex;
          gap: 16px;
        }

        .btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 16px 32px;
          background: var(--accent-blue);
          color: white;
          border: none;
          border-radius: var(--radius-large);
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .btn-primary:hover {
          background: var(--accent-blue-hover);
          transform: translateY(-2px);
        }

        .btn-secondary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 16px 32px;
          background: transparent;
          color: var(--text-primary);
          border: 2px solid var(--border-secondary);
          border-radius: var(--radius-large);
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .btn-secondary:hover {
          background: var(--bg-secondary);
          border-color: var(--accent-blue);
        }

        .hero-image {
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .hero-placeholder {
          width: 100%;
          height: 400px;
          background: var(--bg-secondary);
          border-radius: var(--radius-large);
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
        }

        .stats-card {
          background: var(--bg-primary);
          padding: 32px;
          border-radius: var(--radius-large);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .stat-item {
          text-align: center;
        }

        .stat-number {
          display: block;
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--accent-blue);
        }

        .stat-label {
          font-size: 0.9rem;
          color: var(--text-secondary);
          font-weight: 500;
        }

        .invitation-section {
          padding: 60px 24px;
          background: var(--bg-secondary);
        }

        .invitation-card {
          max-width: 600px;
          margin: 0 auto;
          background: var(--bg-primary);
          padding: 40px;
          border-radius: var(--radius-large);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          text-align: center;
        }

        .invitation-header {
          margin-bottom: 32px;
        }

        .invitation-icon {
          width: 48px;
          height: 48px;
          color: var(--accent-blue);
          margin: 0 auto 16px;
        }

        .invitation-card h2 {
          font-size: 1.8rem;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 8px;
        }

        .invitation-card p {
          color: var(--text-secondary);
          font-size: 1.1rem;
        }

        .invitation-form {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .form-group {
          display: flex;
          gap: 12px;
        }

        .email-input {
          flex: 1;
          padding: 16px 20px;
          background: var(--bg-secondary);
          border: 2px solid var(--border-secondary);
          border-radius: var(--radius-medium);
          color: var(--text-primary);
          font-size: 1rem;
          transition: all 0.2s ease;
        }

        .email-input:focus {
          outline: none;
          border-color: var(--accent-blue);
        }

        .complete-signup-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 16px 24px;
          background: var(--accent-green);
          color: white;
          border: none;
          border-radius: var(--radius-medium);
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .complete-signup-btn:hover:not(:disabled) {
          background: var(--accent-green-hover);
          transform: translateY(-1px);
        }

        .complete-signup-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .features-section,
        .testimonials-section,
        .cta-section {
          padding: 80px 24px;
        }

        .section-header {
          text-align: center;
          margin-bottom: 60px;
        }

        .section-header h2 {
          font-size: 2.5rem;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 16px;
        }

        .section-header p {
          font-size: 1.2rem;
          color: var(--text-secondary);
        }

        .features-grid {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 40px;
        }

        .feature-card {
          text-align: center;
          padding: 40px 24px;
          background: var(--bg-primary);
          border-radius: var(--radius-large);
          transition: all 0.3s ease;
        }

        .feature-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
        }

        .feature-icon {
          width: 80px;
          height: 80px;
          background: var(--accent-blue-bg);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 24px;
          color: var(--accent-blue);
        }

        .feature-card h3 {
          font-size: 1.3rem;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 16px;
        }

        .feature-card p {
          color: var(--text-secondary);
          line-height: 1.6;
        }

        .testimonials-section {
          background: var(--bg-secondary);
        }

        .testimonials-grid {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 32px;
        }

        .testimonial-card {
          background: var(--bg-primary);
          padding: 32px;
          border-radius: var(--radius-large);
          text-align: left;
        }

        .testimonial-rating {
          display: flex;
          gap: 4px;
          margin-bottom: 16px;
        }

        .star-filled {
          color: var(--accent-yellow);
        }

        .testimonial-text {
          font-style: italic;
          color: var(--text-secondary);
          line-height: 1.6;
          margin-bottom: 20px;
          font-size: 1.1rem;
        }

        .testimonial-author strong {
          display: block;
          color: var(--text-primary);
          font-size: 1rem;
          margin-bottom: 4px;
        }

        .testimonial-author span {
          color: var(--text-tertiary);
          font-size: 0.9rem;
        }

        .cta-section {
          background: linear-gradient(135deg, var(--accent-blue) 0%, var(--accent-green) 100%);
          color: white;
        }

        .cta-content {
          max-width: 800px;
          margin: 0 auto;
          text-align: center;
        }

        .cta-section h2 {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 16px;
        }

        .cta-section p {
          font-size: 1.2rem;
          margin-bottom: 32px;
          opacity: 0.9;
        }

        .cta-actions {
          display: flex;
          gap: 16px;
          justify-content: center;
        }

        .cta-section .btn-primary {
          background: white;
          color: var(--accent-blue);
        }

        .cta-section .btn-primary:hover {
          background: #f0f9ff;
        }

        .cta-section .btn-secondary {
          border-color: white;
          color: white;
        }

        .cta-section .btn-secondary:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        @media (max-width: 768px) {
          .hero-content {
            grid-template-columns: 1fr;
            text-align: center;
            gap: 40px;
          }

          .hero-title {
            font-size: 2.5rem;
          }

          .form-group {
            flex-direction: column;
          }

          .features-grid,
          .testimonials-grid {
            grid-template-columns: 1fr;
          }

          .cta-actions {
            flex-direction: column;
            align-items: center;
          }
        }
      `}</style>
    </div>
  );
};